/**
 * Notification Toast Component
 * Shows pop-up notifications for important events
 */

import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  UserPlus,
  MessageSquare,
  MessageCircle,
  CheckCircle,
  Coins,
  Settings,
  Info,
  AlertTriangle,
  XCircle,
  X,
} from "lucide-react";
import { cn } from "@/shared/lib/utils";
import { useNotificationStore } from "../stores/notificationStore";
import type { Notification, NotificationType } from "../types";

const NOTIFICATION_ICON_MAP: Record<NotificationType, React.ElementType> = {
  new_lead: UserPlus,
  session_started: MessageSquare,
  session_message: MessageCircle,
  session_ended: CheckCircle,
  credits_assigned: Coins,
  system: Settings,
  info: Info,
  warning: AlertTriangle,
  error: XCircle,
};

const TOAST_COLORS: Record<NotificationType, string> = {
  new_lead: "border-l-blue-500 bg-blue-50",
  session_started: "border-l-green-500 bg-green-50",
  session_message: "border-l-indigo-500 bg-indigo-50",
  session_ended: "border-l-gray-500 bg-gray-50",
  credits_assigned: "border-l-yellow-500 bg-yellow-50",
  system: "border-l-gray-500 bg-gray-50",
  info: "border-l-blue-500 bg-blue-50",
  warning: "border-l-orange-500 bg-orange-50",
  error: "border-l-red-500 bg-red-50",
};

const ICON_COLORS: Record<NotificationType, string> = {
  new_lead: "text-blue-600",
  session_started: "text-green-600",
  session_message: "text-indigo-600",
  session_ended: "text-gray-600",
  credits_assigned: "text-yellow-600",
  system: "text-gray-600",
  info: "text-blue-600",
  warning: "text-orange-600",
  error: "text-red-600",
};

interface ToastProps {
  notification: Notification;
  onDismiss: () => void;
}

function Toast({ notification, onDismiss }: ToastProps) {
  const navigate = useNavigate();
  const { markAsRead } = useNotificationStore();
  const [isExiting, setIsExiting] = useState(false);

  const Icon = NOTIFICATION_ICON_MAP[notification.type];

  const handleClick = () => {
    markAsRead(notification.id);
    if (notification.link) {
      navigate(notification.link);
    }
    handleDismiss();
  };

  const handleDismiss = () => {
    setIsExiting(true);
    setTimeout(onDismiss, 200); // Wait for exit animation
  };

  // Auto-dismiss after timeout
  useEffect(() => {
    const timeout = notification.autoDismissMs || 5000;
    if (timeout > 0) {
      const timer = setTimeout(handleDismiss, timeout);
      return () => clearTimeout(timer);
    }
  }, [notification.autoDismissMs]);

  return (
    <div
      className={cn(
        "flex items-start gap-3 p-4 rounded-lg shadow-lg border-l-4 max-w-sm cursor-pointer transition-all duration-200",
        TOAST_COLORS[notification.type],
        isExiting
          ? "opacity-0 translate-x-4"
          : "opacity-100 translate-x-0 animate-in slide-in-from-right-4"
      )}
      onClick={handleClick}
      role="alert"
      aria-live="polite"
    >
      <Icon className={cn("h-5 w-5 shrink-0 mt-0.5", ICON_COLORS[notification.type])} />

      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-gray-900">
          {notification.title}
        </p>
        <p className="text-xs text-gray-600 mt-0.5 line-clamp-2">
          {notification.message}
        </p>
        {notification.link && (
          <p className="text-xs text-blue-600 mt-1 font-medium">
            Kliknij, aby zobaczyć szczegóły →
          </p>
        )}
      </div>

      <button
        onClick={(e) => {
          e.stopPropagation();
          handleDismiss();
        }}
        className="p-1 text-gray-400 hover:text-gray-600 transition-colors shrink-0"
        aria-label="Zamknij"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}

export function NotificationToastContainer() {
  const { notifications } = useNotificationStore();
  const [visibleToasts, setVisibleToasts] = useState<Notification[]>([]);
  const [dismissedIds, setDismissedIds] = useState<Set<string>>(new Set());

  // Track new notifications and add them to visible toasts
  useEffect(() => {
    const newNotifications = notifications.filter(
      (n) =>
        !n.read &&
        !dismissedIds.has(n.id) &&
        !visibleToasts.some((t) => t.id === n.id)
    );

    if (newNotifications.length > 0) {
      // Only show the 3 most recent unread notifications
      const toShow = newNotifications.slice(0, 3);
      setVisibleToasts((prev) => [...toShow, ...prev].slice(0, 3));
    }
  }, [notifications]);

  const handleDismiss = (id: string) => {
    setDismissedIds((prev) => new Set([...prev, id]));
    setVisibleToasts((prev) => prev.filter((t) => t.id !== id));
  };

  if (visibleToasts.length === 0) return null;

  return (
    <div
      className="fixed bottom-4 right-4 z-50 flex flex-col gap-2"
      aria-label="Powiadomienia"
    >
      {visibleToasts.map((notification) => (
        <Toast
          key={notification.id}
          notification={notification}
          onDismiss={() => handleDismiss(notification.id)}
        />
      ))}
    </div>
  );
}

export default NotificationToastContainer;
